import { useMemo } from "react"
import { Link } from "react-router-dom"
import { useLang } from "../context/LangContext"

import {
  Cloud,
  Users,
  Workflow,
  Cpu,
  LineChart,
  Truck,
  Shield,
  ListChecks,
  Lightbulb,
  Rocket
} from "lucide-react"


export default function AboutPage() {
  const { lang } = useLang()

  const content = useMemo(() => {
    const es = {
      navLang: "EN",

      heroH1: "El ecosistema donde las ideas se convierten en empresas de alto impacto.",
      heroH2:
        "uCloud360 es la incubadora y aceleradora digital líder para emprendedores en los Estados Unidos. Conectamos tu visión con el conocimiento colectivo y con herramientas tecnológicas propias, diseñadas dentro de la plataforma, para ayudarte a escalar tu negocio en un entorno colaborativo 360°.",
      heroCta: "Únete Gratis Ahora",

      problemTitle: "El Problema",
      problemLead: "¿Te has sentido estancado por no saber qué sigue?",
      problemBody:
        "Emprender en los Estados Unidos es un desafío titánico. Es común sentirse abrumado por preguntas que frenan tu crecimiento:",
      problemQs: [
        "¿Necesito una App o una web?",
        "¿Cómo gestiono mis impuestos?",
        "¿Dónde almaceno mi inventario?",
        "¿Cómo financio mi expansión?"
      ],
      problemClose:
        "Ese sentimiento de incertidumbre no es falta de capacidad, es falta de ecosistema. En uCloud360, eliminamos el miedo al desconocimiento y lo transformamos en una hoja de ruta clara. No tienes que saberlo todo, solo tienes que estar en el lugar correcto.",

      solutionTitle: "La Solución",
      solutionH3: "Mucho más que una red social: Tu Centro de Colaboración Estratégica.",
      solutionBody:
        "uCloud360 es un ecosistema dinámico diseñado para empoderar a emprendedores que, como tú, buscan superar barreras operativas, financieras y tecnológicas.",

      reciproTitle: "El Poder de la Mentoría Recíproca",
      reciproBody:
        "Nuestra filosofía es simple: Tu fortaleza es la solución de otro, y el experto que necesitas ya es parte de nuestra comunidad.",
      reciproPoints: [
        "¿Eres experto en finanzas pero necesitas tecnología? Aquí te conectas con el CTO que resolverá tu infraestructura.",
        "¿Eres un genio tecnológico pero te pierdes en lo legal? Un experto en regulaciones te guiará.",
        "Esta simbiosis garantiza que ningún proyecto se detenga por falta de asesoría técnica o administrativa."
      ],

      channelsTitle: "Canales de Especialidad",
      channelsH3: "Expertise a un clic de distancia.",
      channelsBody:
        "Nuestros canales exclusivos para suscriptores son espacios de alta profesionalización donde la sabiduría colectiva acelera tus resultados:",
      channels: [
        {
          title: "Tech",
          desc: "Optimiza tu infraestructura digital y resuelve retos técnicos con expertos.",
          icon: <Cpu className="h-9 w-9 text-[#2563EB]" />
        },
        {
          title: "Finanzas",
          desc: "Estrategias de inversión, análisis de mercado y gestión de capital.",
          icon: <LineChart className="h-9 w-9 text-[#2563EB]" />
        },
        {
          title: "Logística",
          desc: "Domina tu cadena de suministro, transporte y almacenamiento.",
          icon: <Truck className="h-9 w-9 text-[#2563EB]" />
        },
        {
          title: "Legal",
          desc: "Navega con seguridad entre contratos, propiedad intelectual y regulaciones.",
          icon: <Shield className="h-9 w-9 text-[#2563EB]" />
        },
        {
          title: "Procedimientos",
          desc: "Implementa metodologías de eficiencia operativa y gestión de proyectos.",
          icon: <ListChecks className="h-9 w-9 text-[#2563EB]" />
        }
      ],

      mentorshipTitle: "Mentorías 121",
      mentorshipH3: "Acompañamiento personalizado para decisiones críticas.",
      mentorshipBody:
        "Para aquellos momentos donde necesitas profundidad absoluta, ofrecemos las Mentorías 121. Sesiones privadas y exclusivas a través de e-meetings con expertos de alto nivel, diseñadas para desbloquear el siguiente nivel de tu empresa.",
      mentorshipCta: "Explorar Mentorías 121",

      pricingTitle: "Pricing",
      pricingH3: "Arquitectura de Conversión",
      pricingPlans: [
        {
          badge: "Plan Entry",
          price: "$0",
          cadence: "/ Siempre Gratis",
          bullets: [
            "Únete a la comunidad de emprendedores en EE. UU.",
            "Conecta con profesionales apasionados.",
            "Aprende de la experiencia compartida."
          ],
          cta: "Comenzar Gratis",
          recommended: false
        },
        {
          badge: "Plan Básico",
          price: "$9.99",
          cadence: "/ mes",
          bullets: [
            "Acceso ilimitado a todos nuestros productos de red.",
            "Contenido exclusivo para miembros.",
            "Soporte técnico 24/7.",
            "Acceso prioritario a actualizaciones."
          ],
          cta: "Actualizar ahora",
          recommended: true
        },
        {
          badge: "Plan Premium",
          price: "$29.99",
          cadence: "/ mes",
          bullets: [
            "Todo lo del Plan Básico.",
            "Descuentos exclusivos en Mentorías 121.",
            "Acceso a herramientas tecnológicas Premium.",
            "Invitación prioritaria a eventos presenciales de networking."
          ],
          cta: "Obtener acceso total",
          recommended: false
        }
      ],

      closeTitle: "uCloud360: El activo más valioso de tu negocio es tu red.",
      closeBody:
        "Estamos aquí para desmitificar el emprendimiento. Lo que parece difícil se vuelve alcanzable cuando tienes el apoyo de una comunidad que ya recorrió el camino.",
      closeCta: "Regístrate en uCloud360.com",
      quote:
        "“Si quieres ir rápido, camina solo... Si quieres llegar lejos, ve acompañado.”"
    }

    const en = {
      navLang: "ES",

      heroH1: "The ecosystem where ideas become high-impact companies.",
      heroH2:
        "uCloud360 is the leading digital incubator and accelerator for entrepreneurs in the United States. We connect your vision with collective knowledge and proprietary technological tools, designed within the platform, to help you scale your business in a collaborative 360° environment.",
      heroCta: "Join Free Now",

      problemTitle: "The Problem",
      problemLead: "Have you ever felt stuck because you don’t know what’s next?",
      problemBody:
        "Building a business in the United States is a massive challenge. It’s common to feel overwhelmed by questions that slow down your growth:",
      problemQs: [
        "Do I need an app or a website?",
        "How do I handle taxes correctly?",
        "Where should I store inventory?",
        "How do I finance expansion?"
      ],
      problemClose:
        "That uncertainty is not lack of ability — it’s lack of ecosystem. At uCloud360, we remove the fear of the unknown and transform it into a clear roadmap. You don’t have to know everything — you just need to be in the right place.",

      solutionTitle: "The Solution",
      solutionH3: "More than a social network: your Strategic Collaboration Hub.",
      solutionBody:
        "uCloud360 is a dynamic ecosystem designed to empower entrepreneurs like you to overcome operational, financial, and technological barriers.",

      reciproTitle: "The Power of Reciprocal Mentorship",
      reciproBody:
        "Our philosophy is simple: your strength is someone else’s solution — and the expert you need is already part of the community.",
      reciproPoints: [
        "You’re strong in finance but need technology? Connect with the CTO who can unlock your infrastructure.",
        "You’re great at tech but feel lost legally? A regulations expert will guide you.",
        "This symbiosis ensures no project gets stuck due to missing technical or administrative support."
      ],

      channelsTitle: "Specialty Channels",
      channelsH3: "Expertise, one click away.",
      channelsBody:
        "Our subscriber-only channels are high-value spaces where collective wisdom accelerates results:",
      channels: [
        {
          title: "Tech",
          desc: "Optimize your digital infrastructure and solve technical challenges with experts.",
          icon: <Cpu className="h-9 w-9 text-[#2563EB]" />
        },
        {
          title: "Finance",
          desc: "Investment strategies, market analysis, and capital management.",
          icon: <LineChart className="h-9 w-9 text-[#2563EB]" />
        },
        {
          title: "Logistics",
          desc: "Master your supply chain, transportation, and warehousing.",
          icon: <Truck className="h-9 w-9 text-[#2563EB]" />
        },
        {
          title: "Legal",
          desc: "Navigate contracts, intellectual property, and regulations with confidence.",
          icon: <Shield className="h-9 w-9 text-[#2563EB]" />
        },
        {
          title: "Procedures",
          desc: "Implement operational efficiency methods and project management practices.",
          icon: <ListChecks className="h-9 w-9 text-[#2563EB]" />
        }
      ],

      mentorshipTitle: "1:1 Mentorship",
      mentorshipH3: "Personal guidance for critical decisions.",
      mentorshipBody:
        "For moments that require maximum depth, we offer 1:1 Mentorship. Private, exclusive e-meetings with high-level experts designed to unlock your company’s next level.",
      mentorshipCta: "Explore 1:1 Mentorship",

      pricingTitle: "Pricing",
      pricingH3: "Conversion Architecture",
      pricingPlans: [
        {
          badge: "Entry Plan",
          price: "$0",
          cadence: "/ Forever Free",
          bullets: [
            "Join the US entrepreneur community.",
            "Connect with passionate professionals.",
            "Learn from shared experience."
          ],
          cta: "Start Free",
          recommended: false
        },
        {
          badge: "Basic Plan",
          price: "$9.99",
          cadence: "/ month",
          bullets: [
            "Unlimited access to all network products.",
            "Member-only content.",
            "24/7 technical support.",
            "Priority access to updates."
          ],
          cta: "Upgrade now",
          recommended: true
        },
        {
          badge: "Premium Plan",
          price: "$29.99",
          cadence: "/ month",
          bullets: [
            "Everything in Basic.",
            "Exclusive discounts on 1:1 Mentorship.",
            "Access to premium technology tools.",
            "Priority invitations to in-person networking events."
          ],
          cta: "Get full access",
          recommended: false
        }
      ],

      closeTitle: "uCloud360: your most valuable business asset is your network.",
      closeBody:
        "We’re here to demystify entrepreneurship. What feels difficult becomes achievable when you’re supported by a community that has already walked the path.",
      closeCta: "Sign up at uCloud360.com",
      quote:
        "“If you want to go fast, go alone… If you want to go far, go together.”"
    }

    return lang === "es" ? es : en
  }, [lang])

  const rootClass = "bg-white dark:bg-black text-black dark:text-white"

  const cardClass =
    "rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-neutral-900"

  const mutedText = "text-gray-600 dark:text-gray-300"

  const primaryBtn =
    "inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-medium bg-[#2563EB] text-white hover:bg-[#1d4ed8] transition"

  const secondaryBtn =
    "inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-medium border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-800 transition"

  return (
    <div className={rootClass}>
      {/* 1) HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-14">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight leading-tight">
              {content.heroH1}
            </h1>
            <p className={`mt-5 text-lg leading-relaxed ${mutedText}`}>
              {content.heroH2}
            </p>

            <div className="mt-8 flex items-center gap-3">
              <Link to="/login" className={primaryBtn}>
                {content.heroCta}
              </Link>
              <Link to="/" className={secondaryBtn}>
                {lang === "es" ? "Ver herramientas" : "See tools"}
              </Link>
            </div>
          </div>

          <div className={`${cardClass} p-10`}>
            <div className="flex items-center gap-3">
              <Cloud className="h-10 w-10 text-[#2563EB]" />

              <div>
                <div className="text-sm font-medium">
                  {lang === "es" ? "Incubadora + Aceleradora" : "Incubator + Accelerator"}
                </div>
                <div className={`text-sm ${mutedText}`}>
                  {lang === "es"
                    ? "Ecosistema colaborativo para escalar."
                    : "A collaborative ecosystem built to scale."}
                </div>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                {
                  label: lang === "es" ? "Colaboración" : "Collaboration",
                  icon: <Users className="h-6 w-6 opacity-80" />
                },
                {
                  label: lang === "es" ? "Automatización" : "Automation",
                  icon: <Workflow className="h-6 w-6 opacity-80" />
                },
                {
                  label: lang === "es" ? "Claridad" : "Clarity",
                  icon: <LineChart className="h-6 w-6 opacity-80" />
                },
                {
                  label: lang === "es" ? "Ejecución" : "Execution",
                  icon: <ListChecks className="h-6 w-6 opacity-80" />
                }
              ].map((x) => (
                <div key={x.label} className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                  {x.icon}
                  <div className="mt-3 text-sm font-medium">{x.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2) PROBLEM */}
      <section className="bg-[#F8FAFF] dark:bg-neutral-900/40">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="text-sm font-medium tracking-wide uppercase text-gray-500 dark:text-gray-400">
              {content.problemTitle}
            </div>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight">
              {content.problemLead}
            </h2>
            <p className={`mt-4 ${mutedText} leading-relaxed`}>
              {content.problemBody}
            </p>

            <div className="mt-6 space-y-2">
              {content.problemQs.map((q) => (
                <div key={q} className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-gray-400 dark:bg-gray-600" />
                  <p className={mutedText}>{q}</p>
                </div>
              ))}
            </div>

            <p className={`mt-6 ${mutedText} leading-relaxed`}>
              {content.problemClose}
            </p>
          </div>

          <div className={`${cardClass} p-10`}>
            <div className="flex items-center gap-3">
              <Lightbulb className="h-10 w-10 opacity-80" />

              <div>
                <div className="text-sm font-medium">
                  {lang === "es" ? "De incertidumbre a hoja de ruta" : "From uncertainty to roadmap"}
                </div>
                <div className={`text-sm ${mutedText}`}>
                  {lang === "es"
                    ? "Menos fricción. Más claridad."
                    : "Less friction. More clarity."}
                </div>
              </div>
            </div>

            <div className="mt-7 space-y-3">
              {[
                lang === "es" ? "Define el siguiente paso" : "Define the next step",
                lang === "es" ? "Conecta con quien ya lo resolvió" : "Connect with people who solved it",
                lang === "es" ? "Ejecuta con estructura" : "Execute with structure"
              ].map((x) => (
                <div key={x} className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-sm">
                  {x}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </section>

      {/* 3) SOLUTION + 4) RECIPROCAL MENTORSHIP */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div className={`${cardClass} p-10`}>
            <div className="flex items-center gap-3">
              <Workflow className="h-6 w-6 opacity-80" />
              <div>
                <div className="text-sm font-medium">{content.solutionTitle}</div>
                <div className={`text-sm ${mutedText}`}>
                  {lang === "es" ? "Product-led growth con comunidad." : "Product-led growth powered by community."}
                </div>
              </div>
            </div>

            <h3 className="mt-6 text-2xl font-semibold tracking-tight">
              {content.solutionH3}
            </h3>
            <p className={`mt-4 ${mutedText} leading-relaxed`}>
              {content.solutionBody}
            </p>
          </div>

          <div>
            <div className="text-sm font-medium tracking-wide uppercase text-gray-500 dark:text-gray-400">
              {content.reciproTitle}
            </div>
            <p className={`mt-4 ${mutedText} leading-relaxed`}>
              {content.reciproBody}
            </p>

            <div className="mt-6 space-y-3">
              {content.reciproPoints.map((p) => (
                <div key={p} className={`${cardClass} p-5`}>
                  <p className={mutedText}>{p}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4) SPECIALTY CHANNELS */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-sm font-medium tracking-wide uppercase text-gray-500 dark:text-gray-400">
            {content.channelsTitle}
          </div>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight">
            {content.channelsH3}
          </h2>
          <p className={`mt-4 ${mutedText} leading-relaxed`}>
            {content.channelsBody}
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {content.channels.map((c) => (
            <div key={c.title} className={`${cardClass} p-7`}>
              {c.icon}
              <div className="mt-5 text-lg font-semibold">{c.title}</div>
              <p className={`mt-2 ${mutedText} leading-relaxed`}>{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5) 1:1 MENTORSHIP */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className={`${cardClass} p-10 md:p-12`}>
          <div className="flex items-start justify-between gap-8 flex-col md:flex-row">
            <div>
              <div className="flex items-center gap-3">
                <Rocket className="h-6 w-6 opacity-80" />
                <div className="text-sm font-medium tracking-wide uppercase text-gray-500 dark:text-gray-400">
                  {content.mentorshipTitle}
                </div>
              </div>

              <h3 className="mt-5 text-2xl md:text-3xl font-semibold tracking-tight">
                {content.mentorshipH3}
              </h3>
              <p className={`mt-4 ${mutedText} leading-relaxed max-w-3xl`}>
                {content.mentorshipBody}
              </p>
            </div>

            <div className="flex gap-3">
              <Link to="/login" className={primaryBtn}>
                {content.mentorshipCta}
              </Link>
              <Link to="/about" className={secondaryBtn}>
                {lang === "es" ? "Ver detalles" : "View details"}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 6) PRICING */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-sm font-medium tracking-wide uppercase text-gray-500 dark:text-gray-400">
            {content.pricingTitle}
          </div>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight">
            {content.pricingH3}
          </h2>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {content.pricingPlans.map((p) => (
            <div
              key={p.badge}
              className={[
                cardClass,
                "p-8",
                p.recommended ? "ring-1 ring-black dark:ring-white" : ""
              ].join(" ")}
            >
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">{p.badge}</div>
                {p.recommended && (
                  <div className="text-xs px-2 py-1 rounded-full border border-gray-300 dark:border-gray-700">
                    {lang === "es" ? "Recomendado" : "Recommended"}
                  </div>
                )}
              </div>

              <div className="mt-5 flex items-end gap-2">
                <div className="text-4xl font-semibold tracking-tight">{p.price}</div>
                <div className={`text-sm ${mutedText}`}>{p.cadence}</div>
              </div>

              <div className="mt-6 space-y-3">
                {p.bullets.map((b) => (
                  <div key={b} className="flex items-start gap-3">
                    <span className="mt-1.5 h-2 w-2 rounded-full bg-gray-400 dark:bg-gray-600" />
                    <p className={mutedText}>{b}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Link
                  to="/login"
                  className={p.recommended ? primaryBtn : secondaryBtn}
                >
                  {p.cta}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 7) CLOSE */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className={`${cardClass} p-10 md:p-12`}>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight">
                {content.closeTitle}
              </h2>
              <p className={`mt-4 ${mutedText} leading-relaxed`}>
                {content.closeBody}
              </p>

              <div className="mt-8 flex gap-3">
                <Link to="/login" className={primaryBtn}>
                  {content.closeCta}
                </Link>
                <Link to="/" className={secondaryBtn}>
                  {lang === "es" ? "Volver al inicio" : "Back to home"}
                </Link>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-8">
              <div className="flex items-center gap-3">
                <Users className="h-6 w-6 opacity-80" />
                <div>
                  <div className="text-sm font-medium">
                    {lang === "es" ? "Network effect" : "Network effect"}
                  </div>
                  <div className={`text-sm ${mutedText}`}>
                    {lang === "es"
                      ? "Tu red acelera tus decisiones."
                      : "Your network accelerates your decisions."}
                  </div>
                </div>
              </div>

              <p className={`mt-6 text-lg leading-relaxed ${mutedText}`}>
                {content.quote}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer spacing */}
      <div className="h-14" />
    </div>
  )
}
